/**
 * Standing latency: glass-to-glass delay that no network or decoder meter accounts for.
 *
 * Field shape this names (Android, 2026-09-19): a 21-frame buffer sat at ~320 ms
 * glass-to-glass for 45 s while the standing queue read 0 ms, the playout trend read 0-40 ms,
 * loss read 0 and RTT read 5 ms. The calibrated floor (a window minimum) absorbed the 320 ms
 * and the budget monitor filed it as a route floor - "distance, never acted on". It was not
 * distance. It was frames standing in the receiver's buffer, and it is fixable.
 *
 * This module holds the pure types and thresholds. The classifier and the per-peer detector
 * live beside it (Phase 3). Browser-free and Node/Jest-safe.
 */

/**
 * What the glass-to-glass floor is made of.
 *
 * - `route`: propagation plus decode plus one frame interval explains it. Not fixable by the
 *   encoder; observability only.
 * - `standing-buffer`: the floor sits well above what the route accounts for, while the
 *   trend is flat, the queue is empty and loss is nil. Frames are standing still somewhere on
 *   the receive side. Fixable; must be acted on.
 * - `unknown`: a floor or an RTT floor is not yet trusted.
 */
export type LatencyFloorKind = 'route' | 'standing-buffer' | 'unknown';

/**
 * Where the standing frames appear to sit, judged across frame-rate changes.
 *
 * - `time-constant`: depth in ms holds while fps changes -> a time-based target (jitter buffer
 *   or timing module). Slowing arrival below playout drains it.
 * - `frame-constant`: depth in frames holds while fps changes -> frames in flight inside the
 *   decoder. Fewer fps makes the wait LONGER; only preventing the cause helps.
 * - `undetermined`: not enough fps variation observed to tell.
 */
export type StandingDepthHypothesis = 'time-constant' | 'frame-constant' | 'undetermined';

export type StandingLatencyInputs = {
  /** Only explicitly fresh measurements may confirm a standing buffer. */
  measurementFresh?: boolean;
  /** Calibrated glass-to-glass, latest reading, ms. */
  g2gLastMs?: number;
  /** Calibrated glass-to-glass window minimum (the link's floor), ms. */
  g2gFloorMs?: number;
  /** RTT floor, ms (two-way). One-way propagation is taken as half. */
  rttFloorMs?: number;
  standingQueueMs?: number;
  playoutLatencyTrendMs?: number;
  packetsLostPct?: number;
  decodeMsPerFrame?: number;
  deliveredFps?: number;
  standingBacklogFrames?: number;
  jitterBufferDelayMs?: number;
};

export type StandingLatencyThresholds = {
  /** Excess (the live reading minus everything the route and decoder account for) must reach this. */
  minExcessMs: number;
  /**
   * |playoutLatencyTrendMs| at or below this is flat. Aligned with PLAYOUT_TREND_HYSTERESIS_MS:
   * a trend the congestion ladder itself treats as settled is flat here too.
   */
  flatTrendMs: number;
  /**
   * standingQueueMs at or below this is an empty queue. Aligned with
   * RECOVERY_STANDING_QUEUE_MS_EXIT: what the recovery controller calls "no longer queued".
   */
  maxQueueMs: number;
  /** packetsLostPct at or below this is nil loss. */
  maxLossPct: number;
  /** The condition must hold this long before a detector reports it as begun. */
  holdMs: number;
  /**
   * Once begun, the condition must be absent this long before the detector reports it ended.
   * Field data: a single 34 ms trend tick would otherwise have ended a 45 s standing buffer.
   */
  releaseHoldMs: number;
};

export const DEFAULT_STANDING_LATENCY_THRESHOLDS: StandingLatencyThresholds = {
  minExcessMs: 75,
  flatTrendMs: 50,
  maxQueueMs: 15,
  maxLossPct: 0.5,
  holdMs: 5_000,
  releaseHoldMs: 3_000,
};

export type StandingLatencyVerdict = {
  evidenceState?: 'tentative' | 'confirmed' | 'unavailable';
  kind: LatencyFloorKind;
  /**
   * The fixable part: the glass-to-glass floor minus half the RTT floor, minus decoder
   * residency, minus one frame interval. Null when the floor or the RTT floor is untrusted.
   */
  excessMs: number | null;
  depthHypothesis: StandingDepthHypothesis;
};

export const LATENCY_FLOOR_KINDS: ReadonlyArray<LatencyFloorKind> = ['route', 'standing-buffer', 'unknown'];

export function isLatencyFloorKind(value: unknown): value is LatencyFloorKind {
  return typeof value === 'string' && (LATENCY_FLOOR_KINDS as ReadonlyArray<string>).includes(value);
}
